import { Grid } from '@zillow/constellation';
import type { Answers, RentalApplication, UserStatus } from '../../graphql/queries';
import { GridDesktopOnly } from '../../styles/styled-components';
import { FIELD_IDS, type SectionId, STEP_COMPONENTS, STEP_TIPS } from '../../constants/wizard';
import type { WizardStepType } from '../../models';
import { WizardProgressBar } from './WizardProgressBar';
import { ApplicationOverview } from '../ApplicationOverview';
import { getIsAnswersComplete } from '../../utils/overview/overview-statuses';
import { ReviewStatusText } from './ReviewStatusText';
import { SubmissionCheckbox } from './SubmissionCheckbox';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { useFormContext } from 'react-hook-form';

interface Props {
  application: RentalApplication;
  userStatuses?: Array<UserStatus>;
  onSectionClick: (sectionId: SectionId) => void;
  onPersist: () => Promise<void>;
  givesPermission: boolean;
  setGivesPermission: (givesPermission: boolean) => void;
  step?: WizardStepType;
}

export function ApplicationEdit({
  application,
  userStatuses = [],
  onSectionClick,
  onPersist,
  step,
  givesPermission,
  setGivesPermission,
}: Props) {
  const { rentalListing, listingOwner } = application;
  const applicationContext = useApplicationContext();
  const { watch } = useFormContext();
  const isComplete = getIsAnswersComplete(applicationContext, watch(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS));
  return (
    <>
      <WizardProgressBar stepId={step?.id} onSectionClick={onSectionClick} />
      {step ? (
        <Step application={application} userStatuses={userStatuses} step={step} onPersist={onPersist} />
      ) : (
        <>
          <ReviewStatusText isComplete={isComplete} />
          <ApplicationOverview />
          <SubmissionCheckbox
            disabled={!isComplete}
            givesPermission={givesPermission}
            setGivesPermission={setGivesPermission}
            listingOwnerName={listingOwner?.name || ''}
            rentalListing={rentalListing || {}}
          />
        </>
      )}
    </>
  );
}

function Step({
  application,
  userStatuses,
  step,
  onPersist,
}: {
  application: RentalApplication;
  userStatuses: Array<UserStatus>;
  step: WizardStepType;
  onPersist: () => Promise<void>;
}) {
  const applicationId = application.id;
  const rentalListing = application.rentalListing;
  if (!applicationId) return null;

  const Component = STEP_COMPONENTS[step.id as keyof typeof STEP_COMPONENTS];
  const StepTipComponent = STEP_TIPS[step.id as keyof typeof STEP_TIPS];
  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" paddingBottom="sm" gap="md">
      <GridDesktopOnly gridColumnEnd={{ xl_gte: 'span 1' }} />
      <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 8', xl_gte: 'span 7' }}>
        <Component
          applicationId={applicationId}
          answers={application.answers as Answers}
          isPrimaryApplicant={!!application.isPrimaryApplicant}
          userStatuses={userStatuses}
          rentalListing={rentalListing}
          onPersist={onPersist}
        />
      </Grid>

      <Grid gridColumnEnd={{ default: `span 12`, lg: 'span 4', xl_gte: 'span 3' }}>
        {StepTipComponent && (
          <StepTipComponent applicationId={applicationId} userStatuses={userStatuses} onPersist={onPersist} />
        )}
      </Grid>
      <GridDesktopOnly gridColumnEnd={{ xl_gte: 'span 1' }} />
    </Grid>
  );
}
